import React, { useEffect, useRef } from 'react';
import styles from './AboutPage.module.css';

const AboutPage: React.FC = () => {
  const pageRef = useRef<HTMLElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    const container = pageRef.current;
    if (!container) return;

    const visibleClass = styles.visible;
    const elementsToObserve = Array.from(container.querySelectorAll('section[id]'));
    const nav = container.querySelector('nav');
    const navPath = nav?.querySelector('svg path') as SVGPathElement | null;

    if (!nav) return;

    const navListItems = Array.from(nav.querySelectorAll('li'));
    const navItems = navListItems
      .map(listItem => {
        const anchor = listItem.querySelector('a');
        const targetID = anchor?.getAttribute('href')?.slice(1);
        const target = targetID ? document.getElementById(targetID) : null;
        return { listItem, anchor, target, pathStart: 0, pathEnd: 0 };
      })
      .filter(item => item.target && item.anchor);

    function drawPath() {
        if (!navPath) return;
        let path: (string | number)[] = [];
        let pathIndent: number | undefined;

        navItems.forEach((item, i) => {
            if (!item.anchor) return;
            const x = item.anchor.offsetLeft - 5;
            const y = item.anchor.offsetTop;
            const height = item.anchor.offsetHeight;

            if (i === 0) {
            path.push('M', x, y, 'L', x, y + height);
            item.pathStart = 0;
            } else {
            if (pathIndent !== x && pathIndent !== undefined) path.push('L', pathIndent, y);
            path.push('L', x, y);
            navPath.setAttribute('d', path.join(' '));
            item.pathStart = navPath.getTotalLength() || 0;
            path.push('L', x, y + height);
            }
            pathIndent = x;
            navPath.setAttribute('d', path.join(' '));
            item.pathEnd = navPath.getTotalLength();
        });
    }

    function syncPath() {
        if (!navPath) return;
        const someElsAreVisible = () => nav!.querySelectorAll(`.${visibleClass}`).length > 0;
        const thisElIsVisible = (el: HTMLElement) => el.classList.contains(visibleClass);
        const pathLength = navPath.getTotalLength();

        let pathStart = pathLength;
        let pathEnd = 0;
        let lastPathStart: number | undefined;
        let lastPathEnd: number | undefined;

        navItems.forEach(item => {
            if (thisElIsVisible(item.listItem)) {
            pathStart = Math.min(item.pathStart, pathStart);
            pathEnd = Math.max(item.pathEnd, pathEnd);
            }
        });

        if (someElsAreVisible() && pathStart < pathEnd) {
            if (pathStart !== lastPathStart || pathEnd !== lastPathEnd) {
            const dashArray = `1 ${pathStart} ${pathEnd - pathStart} ${pathLength}`;
            navPath.style.setProperty('stroke-dashoffset', '1');
            navPath.style.setProperty('stroke-dasharray', dashArray);
            navPath.style.setProperty('opacity', '1');
            }
        } else {
            navPath.style.setProperty('opacity', '0');
        }

        lastPathStart = pathStart;
        lastPathEnd = pathEnd;
    }

    function markVisibleSection(observedEls: IntersectionObserverEntry[]) {
      if (!container) return;
      observedEls.forEach(observedEl => {
        const id = observedEl.target.getAttribute('id');
        const anchor = container.querySelector(`nav li a[href="#${id}"]`);
        if (!anchor) return;
        const listItem = anchor.parentElement;
        if (!listItem) return;
        
        if (observedEl.isIntersecting) {
          listItem.classList.add(visibleClass);
        } else {
          listItem.classList.remove(visibleClass);
        }
        syncPath();
      });
    }
    
    // Only run drawing logic if SVG is present (i.e., on desktop)
    if (navPath) {
        drawPath();
        window.addEventListener('resize', drawPath);
    }
    
    observerRef.current = new IntersectionObserver(markVisibleSection, {
      root: null, // observes intersections in the viewport
      rootMargin: "-40% 0px -40% 0px", // triggers when element is in the middle 20% of the viewport
      threshold: 0
    });
    elementsToObserve.forEach(thisEl => observerRef.current?.observe(thisEl));

    return () => {
      observerRef.current?.disconnect();
      if (navPath) {
          window.removeEventListener('resize', drawPath);
      }
    };
  }, []);

 return (
  <main ref={pageRef} className={styles.pageWrapper}>
    <nav className={styles.progressNav}>
      <ul>
        <li><a href="#intro">A História</a></li>
        <li>
          <a href="#cultural">Cultura & Posicionamento</a>
          <ul>
            <li><a href="#identidade">Identidade Alternativa</a></li>
            <li><a href="#autenticidade">Estética e Autenticidade</a></li>
          </ul>
        </li>
        <li>
          <a href="#areas">Áreas de Atuação</a>
          <ul>
            <li><a href="#musica">Música</a></li>
            <li><a href="#moda">Moda</a></li>
            <li><a href="#tecnologia">Tecnologia</a></li>
            <li><a href="#pensamento">Pensamento Crítico</a></li>
          </ul>
        </li>
        <li><a href="#second">O Que Fazemos</a></li>
        <li><a href="#third">Plug-ins Clydes</a></li>
        <li><a href="#impacto">Impacto Criativo</a></li>
        <li><a href="#essencia">Nossa Essência</a></li>
      </ul>
      <svg xmlns="http://www.w3.org/2000/svg">
        <path />
      </svg>
    </nav>

    <article className={styles.contentArticle} id="top">
      <section id="intro">
        <h2>A História: Uma Ideia Urbana</h2>
        <p>A Clydes nasceu em 2024 como uma resposta à desigualdade de acesso — criativa, tecnológica e cultural. Não somos uma marca comum. Somos uma ideia urbana que encontrou forma e propósito: tornar o inacessível acessível.</p>
        <p>Transformamos o pouco em muito. Criamos caminhos onde antes havia muros. E mostramos, na prática, que é possível fazer diferente com o que se tem.</p>
      </section>

      <section id="cultural">
        <h2>Cultura e Posicionamento</h2>
        <p>Mais do que vender produtos, queremos desbloquear possibilidades. A Clydes é um selo de cultura alternativa que atua com consistência, sem depender de tendências ou do que o mercado espera.</p>
      </section>

      <section id="identidade">
        <h2>Identidade Alternativa</h2>
        <p>Assumimos uma estética própria, feita para quem se identifica com a criação livre e fora dos padrões. Cada decisão visual ou funcional tem intenção.</p>
      </section>

      <section id="autenticidade">
        <h2>Estética e Autenticidade</h2>
        <p>Não seguimos fórmulas. Cada produto, roupa ou ferramenta que lançamos reflete a cultura urbana e o compromisso com o que é verdadeiro.</p>
      </section>

      <section id="areas">
        <h2>Áreas de Atuação</h2>
        <p>Transitamos naturalmente entre diferentes universos criativos. Essas áreas não nos limitam — elas nos expandem.</p>
      </section>

      <section id="musica">
        <h2>Música</h2>
        <p>Do beat ao plugin, da produção independente ao palco underground, a música é um dos nossos pilares. Criamos ferramentas e espaços pra ela acontecer.</p>
      </section>

      <section id="moda">
        <h2>Moda</h2>
        <p>Nossas roupas são declarações visuais. Simbólicas, urbanas e feitas pra quem quer vestir identidade, não tendência.</p>
      </section>

      <section id="tecnologia">
        <h2>Tecnologia</h2>
        <p>Desenvolvemos soluções práticas e acessíveis que ampliam o poder de criação. Plugins, automações, proteção de beats e mais.</p>
      </section>

      <section id="pensamento">
        <h2>Pensamento Crítico</h2>
        <p>Criatividade também é reflexão. Usamos arte, som e estética pra provocar, levantar temas e desconstruir fórmulas.</p>
      </section>

      <section id="second">
        <h2>O Que Fazemos</h2>
        <ul>
          <li>Plugins criativos para músicos e produtores independentes</li>
          <li>Lançamento de novos artistas com autenticidade sonora</li>
          <li>Músicas fora do padrão, que desafiam o mercado</li>
          <li>Eventos underground e experiências culturais</li>
          <li>Moda urbana simbólica, com design forte e sutil</li>
          <li>Conteúdo provocador que estimula a reflexão</li>
        </ul>
      </section>

      <section id="third">
        <h2>Plug-ins Clydes: A Nova Geração</h2>
        <p>Ferramentas que não só funcionam — libertam ideias.</p>
        <ul>
          <li><strong>KitSync</strong> – Gerenciamento de drumkits em tempo real</li>
          <li><strong>Mix Ready Vox</strong> – Guia inteligente para vocais de alta qualidade</li>
          <li><strong>SampleExtend</strong> – Geração de continuações naturais de samples</li>
          <li><strong>Auto Protect Beat</strong> – Proteção de beats com marca d’água invisível</li>
          <li><strong>Reference Chain Copier</strong> – Cópia de cadeias de efeitos entre DAWs</li>
          <li><strong>Sombra & VendingBeats</strong> – Sussurros criativos e desafios artísticos para combater o bloqueio criativo</li>
        </ul>
      </section>

      <section id="impacto">
        <h2>Impacto Criativo</h2>
        <p>Cada produto da Clydes é pensado para desbloquear algo. Seja um beat inacabado, uma ideia travada, uma identidade artística não expressa. Nós criamos com quem cria — com o que tem e como pode.</p>
      </section>

      <section id="essencia">
        <h2>Nossa Essência</h2>
        <p>A Clydes não vende produto. Entregamos propósito.</p>
        <p>Somos um espaço de criação. Um selo para quem vive a rua, a batida, o visual e o silêncio. Para quem cria com alma, sem pedir licença.</p>
        <p>Clydes é visceral. É símbolo. É som.</p>
        <p><strong>Clydes — a democracia do inacessível.</strong></p>
      </section>
    </article>
  </main>
  );
};

export default AboutPage;